// client/src/components/manga/MangaDashboard.js
import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';

const MangaDashboard = ({ wishlist = false }) => {
  const { featureFlags } = useFeatureFlags();

  if (!featureFlags.enableMangaFeature) {
    return <Navigate to="/" replace />;
  }

  const filterOptions = [
    { value: '', label: 'All Demographics' },
    { value: 'Shonen', label: 'Shonen' },
    { value: 'Shojo', label: 'Shojo' },
    { value: 'Seinen', label: 'Seinen' },
    { value: 'Josei', label: 'Josei' },
    { value: 'Kodomomuke', label: 'Kodomomuke' },
    { value: 'Other', label: 'Other' },
  ];

  const proxyImage = (url) => url ? `/api/proxy-image?url=${encodeURIComponent(url)}` : '';

  const renderProps = {
    getImageUrl: (item) => item.processedImageUrl || (item.imageUrl ? proxyImage(item.imageUrl) : ''),
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      const vol = item.totalVolumes
        ? `Vol. ${item.volume} of ${item.totalVolumes}`
        : `Vol. ${item.volume}`;
      const parts = [vol];
      if (item.demographic) parts.push(item.demographic);
      if (item.format && item.format !== 'Tankōbon') parts.push(item.format);
      return parts.join(' · ');
    },
    getItemDetails: (item) => {
      const parts = [];
      if (item.author) parts.push(item.author);
      if (item.condition) parts.push(item.condition);
      if (item.read) parts.push('Read');
      return parts.join(' · ');
    },
  };

  const getFilterValue = (item) => item.demographic;

  const editFields = [
    { name: 'name',   label: 'Series Title', type: 'text', required: true, gridProps: { xs: 12 } },
    { name: 'volume', label: 'Volume Number', type: 'number', required: true, gridProps: { xs: 12, sm: 6 } },
    { name: 'totalVolumes', label: 'Total Volumes in Series', type: 'number', gridProps: { xs: 12, sm: 6 } },
    {
      name: 'demographic', label: 'Demographic', type: 'select',
      options: [
        { value: '', label: 'Unknown' },
        { value: 'Shonen', label: 'Shonen' },
        { value: 'Shojo', label: 'Shojo' },
        { value: 'Seinen', label: 'Seinen' },
        { value: 'Josei', label: 'Josei' },
        { value: 'Kodomomuke', label: 'Kodomomuke' },
        { value: 'Other', label: 'Other' },
      ],
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'format', label: 'Format', type: 'select',
      options: ['Tankōbon', 'Omnibus', 'Deluxe Edition', 'Box Set', 'Other'].map(v => ({ value: v, label: v })),
      gridProps: { xs: 12, sm: 6 },
    },
    { name: 'author',    label: 'Author / Mangaka', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'publisher', label: 'Publisher',         type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'year',     label: 'Publication Year',  type: 'number', gridProps: { xs: 12, sm: 6 } },
    { name: 'language', label: 'Language',           type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'genre',    label: 'Genre',              type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'isbn13',   label: 'ISBN-13',            type: 'text', gridProps: { xs: 12, sm: 6 } },
    {
      name: 'condition', label: 'Condition', type: 'select',
      options: [
        { value: 'Mint (M)',         label: 'Mint (M)' },
        { value: 'Near Mint (NM)',   label: 'Near Mint (NM)' },
        { value: 'Very Good (VG)',   label: 'Very Good (VG)' },
        { value: 'Good (G)',         label: 'Good (G)' },
        { value: 'Fair (F)',         label: 'Fair (F)' },
        { value: 'Poor (P)',         label: 'Poor (P)' },
      ],
      gridProps: { xs: 12, sm: 6 },
    },
    { name: 'read',      label: 'Read',       type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
    { name: 'imageUrl',  label: 'Image URL',  type: 'text', gridProps: { xs: 12 } },
    { name: 'price',     label: 'Price Paid', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'rating',    label: 'Rating',     type: 'rating', gridProps: { xs: 12 } },
    { name: 'notes',     label: 'Notes',      type: 'textarea', gridProps: { xs: 12 } },
  ];

  const tableColumns = {
    name:        { label: 'Series',      sortable: true },
    volume:      { label: 'Vol.',        sortable: true, numeric: true },
    author:      { label: 'Author',      sortable: true },
    demographic: { label: 'Demographic', sortable: true },
    format:      { label: 'Format',      sortable: true },
    condition:   { label: 'Condition',   sortable: true },
  };

  return (
    <CollectionPage
      collectionType={wishlist ? 'manga-wishlist' : 'manga'}
      title={wishlist ? 'Manga Wishlist' : 'Manga Collection'}
      renderProps={renderProps}
      filterOptions={filterOptions}
      getFilterValue={getFilterValue}
      editFields={editFields}
      tableColumns={tableColumns}
    />
  );
};

export default MangaDashboard;
